import { Link } from "react-router-dom";

/**
 * PublicFooter — Marketing site footer
 *
 * 4-column grid layout with product links, developer resources,
 * company info, and social/connect links.
 * Bottom bar: copyright, privacy & terms links.
 */

interface FooterSection {
  title: string;
  links: Array<{ label: string; href: string; external?: boolean }>;
}

const sections: FooterSection[] = [
  {
    title: "Product",
    links: [
      { label: "Features", href: "/features" },
      { label: "Pricing", href: "/pricing" },
      { label: "Security", href: "/security" },
      { label: "Changelog", href: "#" },
    ],
  },
  {
    title: "Developers",
    links: [
      { label: "Documentation", href: "/developers" },
      { label: "API Reference", href: "/developers" },
      { label: "GitHub", href: "#", external: true },
      { label: "Status", href: "#", external: true },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "Research", href: "/research" },
      { label: "Careers", href: "/about" },
      { label: "Contact", href: "/about" },
    ],
  },
  {
    title: "Connect",
    links: [
      { label: "Twitter / X", href: "#", external: true },
      { label: "Discord", href: "#", external: true },
      { label: "Telegram", href: "#", external: true },
    ],
  },
];

export function PublicFooter() {
  return (
    <footer className="border-t border-border bg-background">
      {/* Link grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="text-heading-sm text-foreground mb-4">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-body-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-body-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-body-sm text-muted-foreground">
            <p>
              &copy; 2026 DeTrust AI. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/legal/privacy"
                className="hover:text-foreground transition-colors underline-offset-4 hover:underline"
              >
                Privacy Policy
              </Link>
              <Link
                to="/legal/terms"
                className="hover:text-foreground transition-colors underline-offset-4 hover:underline"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
